import type { BookingSelection, DateOnly, DerivedDates } from "./types.ts"

const DAY_MS = 86_400_000

function dateParts(value: string): [number, number, number] {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value)
  if (!match) throw new Error("INVALID_DATE")
  const year = Number(match[1])
  const month = Number(match[2])
  const day = Number(match[3])
  const timestamp = Date.UTC(year, month - 1, day)
  const checked = new Date(timestamp)
  if (
    checked.getUTCFullYear() !== year ||
    checked.getUTCMonth() !== month - 1 ||
    checked.getUTCDate() !== day
  ) throw new Error("INVALID_DATE")
  return [year, month, day]
}

function toEpochDay(value: string): number {
  const [year, month, day] = dateParts(value)
  return Math.floor(Date.UTC(year, month - 1, day) / DAY_MS)
}

function fromEpochDay(value: number): DateOnly {
  const date = new Date(value * DAY_MS)
  const year = String(date.getUTCFullYear()).padStart(4, "0")
  const month = String(date.getUTCMonth() + 1).padStart(2, "0")
  const day = String(date.getUTCDate()).padStart(2, "0")
  return `${year}-${month}-${day}` as DateOnly
}

export function assertDateOnly(value: unknown): DateOnly {
  if (typeof value !== "string") throw new Error("INVALID_DATE")
  dateParts(value)
  return value as DateOnly
}

export function addDays(value: DateOnly, days: number): DateOnly {
  if (!Number.isInteger(days)) throw new Error("INVALID_DAY_OFFSET")
  return fromEpochDay(toEpochDay(value) + days)
}

export function isoWeekday(value: DateOnly): number {
  const weekday = new Date(toEpochDay(value) * DAY_MS).getUTCDay()
  return weekday === 0 ? 7 : weekday
}

export function bookingWindowDays(selection: BookingSelection): number {
  const deliveryDate = assertDateOnly(selection.deliveryDate)
  const collectionDate = assertDateOnly(selection.collectionDate)
  return toEpochDay(collectionDate) - toEpochDay(deliveryDate) + 1
}

export function bookingHireDays(selection: BookingSelection, chargeLogisticsDays = false): number {
  return Math.max(0, bookingWindowDays(selection) - (chargeLogisticsDays ? 0 : 2))
}

export function deriveBookingDates(
  selection: BookingSelection,
  turnaroundDays: number,
  minimumBookingDays = 2,
): DerivedDates {
  if (!Number.isInteger(turnaroundDays) || turnaroundDays < 0) throw new Error("INVALID_TURNAROUND")

  const deliveryDate = assertDateOnly(selection.deliveryDate)
  const collectionDate = assertDateOnly(selection.collectionDate)
  const windowDays = bookingWindowDays(selection)
  if (windowDays < 2) throw new Error("COLLECTION_TOO_EARLY")
  if (!Number.isInteger(minimumBookingDays) || minimumBookingDays < 2) throw new Error("INVALID_MINIMUM_BOOKING_DAYS")
  if (windowDays < minimumBookingDays) throw new Error("HIRE_WINDOW_TOO_SHORT")

  return {
    deliveryDate,
    hireStartDate: deliveryDate,
    hireEndDate: collectionDate,
    collectionDate,
    availabilityEndDateExclusive: addDays(collectionDate, 1 + turnaroundDays),
  }
}

export function shiftSelection(selection: BookingSelection, days: number): BookingSelection {
  return {
    ...selection,
    deliveryDate: addDays(selection.deliveryDate, days),
    collectionDate: addDays(selection.collectionDate, days),
  }
}
